import { useEffect, useState, useCallback } from "react";
import { usDate } from "@/lib/dates";
import { viewFromApi } from "@/components/FileViewer";
import { FileCheck2, Eye, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function ExcuseRequestsCard() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [review, setReview] = useState(null); // {rec, approve}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/excuse-requests", { params: { status } }).then(({ data }) => setRows(data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);

  const openFile = (id) => viewFromApi(api, `/excuse-requests/${id}/file`, { title: "Excuse note attachment" });

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/excuse-requests/${review.rec.id}/${review.approve ? "approve" : "decline"}`, { note });
      toast.success(review.approve ? "Excuse approved — absence marked excused" : "Request declined");
      setReview(null); setNote(""); load();
      window.dispatchEvent(new Event("logerd:excuses-updated"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-indigo-200 bg-indigo-50/40 overflow-hidden mb-6" data-testid="excuse-requests-card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-indigo-100">
        <div className="flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-indigo-700" />
          <span className="font-display font-semibold text-indigo-900">Parent excuse notes</span>
          {status === "pending" && <span className="inline-flex rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold text-white" data-testid="excuse-requests-count">{rows.length} waiting</span>}
        </div>
        <div className="flex gap-1 text-xs">
          {["pending", "approved", "declined"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`excuse-filter-${s}`} className={`rounded-full px-3 py-1 font-medium capitalize ${status === s ? "bg-indigo-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="excuse-requests-table">
          <thead className="text-left text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-2">Student</th><th className="px-4 py-2">Date(s)</th><th className="px-4 py-2">Reason</th><th className="px-4 py-2">From</th><th className="px-4 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-indigo-100">
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-xs">Nothing here.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="bg-white/60" data-testid={`excuse-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{r.student_name}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-slate-700">{usDate(r.date)}{r.end_date ? ` → ${usDate(r.end_date)}` : ""}</td>
                <td className="px-4 py-2.5 text-slate-700 max-w-[320px]"><div className="truncate" title={r.reason}>{r.reason}</div>{r.review_note && <div className="text-[11px] text-slate-500 italic">“{r.review_note}”</div>}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{r.parent_name || "Parent"}<div>{new Date(r.created_at).toLocaleDateString()}</div></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {r.has_doc && <button onClick={() => openFile(r.id)} data-testid={`excuse-file-${r.id}`} title="View attachment" className="h-8 w-8 rounded-lg text-slate-600 hover:bg-white inline-flex items-center justify-center"><Eye className="h-4 w-4" /></button>}
                  {r.status === "pending" ? (
                    <>
                      <button onClick={() => { setReview({ rec: r, approve: true }); setNote(""); }} data-testid={`excuse-approve-${r.id}`} className="ml-1 inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2.5 h-8 text-xs font-semibold hover:bg-emerald-700"><Check className="h-3.5 w-3.5" /> Approve</button>
                      <button onClick={() => { setReview({ rec: r, approve: false }); setNote(""); }} data-testid={`excuse-decline-${r.id}`} className="ml-1 inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white text-rose-700 px-2.5 h-8 text-xs font-semibold hover:bg-rose-50"><X className="h-3.5 w-3.5" /> Decline</button>
                    </>
                  ) : <span className={`text-xs font-semibold ${r.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{r.status} · {r.reviewed_by_name}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="sm:max-w-md" data-testid="excuse-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{review?.approve ? "Approve excuse" : "Decline excuse"} · {review?.rec.student_name}</DialogTitle>
            <DialogDescription>{review?.approve ? "The absence will be recorded as excused and the parent notified." : "The parent will be notified that the note was not accepted."}</DialogDescription>
          </DialogHeader>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Optional note to the parent" data-testid="excuse-review-note" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
          <button onClick={confirm} disabled={busy} data-testid="excuse-review-confirm" className={`w-full h-11 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${review?.approve ? "bg-emerald-600" : "bg-rose-600"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : review?.approve ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />} {review?.approve ? "Approve & mark excused" : "Decline"}
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}
